import React, { useState, useRef, useEffect, useCallback } from "react";
import { NavLink, withRouter } from "react-router-dom";
import { compose } from "react-apollo";
import { withStore } from "../../store";
import classnames from "classnames";
import { SVGImage } from "../../assets/images";
import { fetchHandler, getClientApiURL } from "../../utils";
import { headerConfig, NavItem } from "../../config/headerConfig";
import { captureLoginTrigger } from "../Login/loginTriggerCapture";
import "./Header.scss";
import { metUrl } from "../../config/metMainSite";

interface HeaderProps {
  showLogIn: () => void;
  handleLogout: () => void;
  store: {
    displayLogIn: boolean;
    inMaintenance: boolean;
    user: any;
    userFetchPending: boolean;
  };
  history: any;
}

function Header(props: HeaderProps) {
  const { showLogIn, handleLogout, store } = props;
  const { displayLogIn, inMaintenance, user, userFetchPending } = store;

  const isHomepage = props.history.location.pathname === "/";

  const [menuOpen, setMenuOpen] = useState(false);
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
  const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [heroActive, setHeroActive] = useState(isHomepage);

  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const lastFocusedElement = useRef<Element | null>(null);
  // Map of nav item id → trigger button element, for returning focus on Escape
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const getFocusableElements = useCallback(function(container: HTMLElement): HTMLElement[] {
    const selectors = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ];
    return Array.from(
      container.querySelectorAll<HTMLElement>(selectors.join(","))
    ).filter(function(el) { return !el.hasAttribute("disabled") && el.tabIndex !== -1; });
  }, []);

  const hideMenu = useCallback(function() {
    const body = document.querySelector("body");
    if (body) { body.classList.remove("header-nav-open"); }
    setMenuOpen(false);
    setOpenSubmenuId(null);
  }, []);

  const showMenu = useCallback(function() {
    const body = document.querySelector("body");
    if (body) { body.classList.add("header-nav-open"); }
    setMenuOpen(true);
  }, []);

  const handleShowLogIn = useCallback(function() {
    hideMenu();
    showLogIn();
  }, [hideMenu, showLogIn]);

  const scrollToTop = useCallback(function() {
    hideMenu();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [hideMenu]);

  const onLogOut = useCallback(function() {
    hideMenu();
    const logoutUrl = getClientApiURL("/logout");
    fetchHandler(logoutUrl, {}, handleLogout);
  }, [hideMenu, handleLogout]);

  const toggleSubmenu = useCallback(function(id: string) {
    setOpenSubmenuId(function(prev) { return prev === id ? null : id; });
  }, []);

  const openSubmenu = useCallback(function(id: string) {
    setOpenSubmenuId(id);
  }, []);

  // MET2-2812: clear this submenu on mouse-leave ONLY if it's the open one,
  // without moving focus (only Escape restores focus to the trigger).
  const closeOnLeave = useCallback(function(id: string) {
    setOpenSubmenuId(function(prev) { return prev === id ? null : prev; });
  }, []);

  // Close a desktop submenu and return focus to its trigger button
  const closeSubmenu = useCallback(function(id: string) {
    setOpenSubmenuId(null);
    const trigger = triggerRefs.current.get(id);
    if (trigger) { trigger.focus(); }
  }, []);

  const handleSearchSubmit = useCallback(function(e: React.FormEvent) {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      window.location.href = "/search?q=" + encodeURIComponent(q);
    }
  }, [searchQuery]);

  const onMenuKeyDown = useCallback(function(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      hideMenu();
      return;
    }
    if (event.key !== "Tab") return;
    const container = menuContainerRef.current;
    const toggle = menuToggleRef.current;
    if (!container) return;
    const focusable = getFocusableElements(container);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement;
    if (event.shiftKey) {
      // Shift+Tab from first item or toggle → send to the other end
      if (active === first) {
        event.preventDefault();
        (toggle || last).focus();
      } else if (active === toggle) {
        event.preventDefault();
        last.focus();
      }
      return;
    }
    // Tab from last item or toggle → send to the other end
    if (active === last) {
      event.preventDefault();
      (toggle || first).focus();
    } else if (active === toggle) {
      event.preventDefault();
      first.focus();
    }
  }, [hideMenu, getFocusableElements]);

  useEffect(function() {
    if (menuOpen) {
      lastFocusedElement.current = document.activeElement;
      const container = menuContainerRef.current;
      if (container) {
        const focusable = getFocusableElements(container);
        if (focusable.length > 0) { focusable[0].focus(); }
      }
      document.addEventListener("keydown", onMenuKeyDown);
    } else {
      document.removeEventListener("keydown", onMenuKeyDown);
      const el = lastFocusedElement.current;
      if (el && (el as HTMLElement).focus) {
        (el as HTMLElement).focus();
      } else if (menuToggleRef.current) {
        menuToggleRef.current.focus();
      }
      lastFocusedElement.current = null;
    }
    return function() {
      document.removeEventListener("keydown", onMenuKeyDown);
    };
  }, [menuOpen, onMenuKeyDown, getFocusableElements]);

  useEffect(function() {
    return function() {
      const body = document.querySelector("body");
      if (body) { body.classList.remove("header-nav-open"); }
    };
  }, []);

  // MET2-2840: close mobile menu when viewport expands past the mobile breakpoint
  useEffect(function() {
    function onResize() {
      if (window.innerWidth > 1280 && menuOpen) {
        hideMenu();
      }
    }
    window.addEventListener("resize", onResize);
    return function() { window.removeEventListener("resize", onResize); };
  }, [menuOpen, hideMenu]);

  // Keep heroActive in sync when navigating to/from the homepage
  useEffect(function() {
    setHeroActive(isHomepage);
  }, [isHomepage]);

  // Listen for Hero's scroll-driven hero-active changes
  useEffect(function() {
    function onHeroActive(e: Event) {
      setHeroActive((e as CustomEvent<{ active: boolean }>).detail.active);
    }
    document.addEventListener("metopera:hero-active", onHeroActive);
    return function() {
      document.removeEventListener("metopera:hero-active", onHeroActive);
    };
  }, []);

  useEffect(function() {
    if (desktopSearchOpen && searchInputRef.current) {
      // Delay focus until after the 0.3s max-width transition completes,
      // otherwise the browser scrolls the page to reveal the still-hidden input.
      const timer = setTimeout(function() {
        if (searchInputRef.current) { searchInputRef.current.focus(); }
      }, 300);
      return function() { clearTimeout(timer); };
    }
  }, [desktopSearchOpen]);

  const navContainerClass = classnames("Header", "nav-container", {
    "nav-menu-is-open": menuOpen,
    "nav-desktop-search-is-open": desktopSearchOpen,
    "login-page-header": displayLogIn,
    "hero-page": isHomepage,
    "hero-active": heroActive,
  });

  return (
    <React.Fragment>
      <a
        href="#main-content"
        className="skip-link"
        onClick={function(e) {
          // MET2-2873: move focus to the ACTIVE hero slide's first actionable
          // control, not just scroll. Per Sean Havrilla (#metopera-c 2026-07-01)
          // the intended target is the hero's first option in display order:
          // "Start free 7-day trial" when logged out, the play/watch link when
          // logged in. Scoping to the active slide (rather than the first
          // focusable in <main>) avoids landing on the account/subscriber
          // control that renders ahead of the hero for logged-in users.
          // Falls back to the first actionable control in <main>, then <main>.
          // Keep href for no-JS / screen-reader use.
          e.preventDefault();
          const main = document.getElementById("main-content");
          if (!main) return;
          main.scrollIntoView();
          const sel = "a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])";
          const actionable = function(el: HTMLElement) {
            if (el.tabIndex === -1) return false;
            if (el.closest("[inert]") || el.closest("[aria-hidden='true']")) return false;
            const r = el.getBoundingClientRect();
            return r.width > 0 && r.height > 0;
          };
          const activeSlide = main.querySelector<HTMLElement>(".Slide.info-active, .Slide.active");
          const scope: ParentNode = activeSlide || main;
          let target = Array.from(scope.querySelectorAll<HTMLElement>(sel)).filter(actionable)[0];
          if (!target) target = Array.from(main.querySelectorAll<HTMLElement>(sel)).filter(actionable)[0];
          (target || main).focus();
        }}
      >Skip to main content</a>
      <header id="app-header" role="banner" className={navContainerClass}>

        {/* Utility bar — desktop only, black background */}
        {!inMaintenance && !displayLogIn && (
          <div className="nav-utility-container">
            <div className="header-row">
              {/* MET2-2850: utility nav is plain navigation, not an application menubar.
                  Dropped role="menubar" (and role="menuitem" on children) because the menubar
                  pattern forbids non-menu descendants like the search form's input. The <nav>
                  ancestor + aria-label still provides landmark semantics. */}
              <ul className="nav-utility" aria-label="Utility navigation">

                {/* Desktop search — dropped role="none"/role="menuitem" since the parent
                    ul is no longer role="menubar". */}
                <li className="nav-utility-item nav-search-container">
                  <a
                    href={metUrl("/search")}
                    className="nav-utility-link nav-utility-link-search"
                  >
                    <span>Search</span>
                  </a>
                </li>

                {/* Login / Account */}
                <li className="nav-utility-item">
                  {!userFetchPending && !user && (
                    <button
                      type="button"
                      className="nav-utility-link nav-utility-link-account"
                      onClick={function(e) {
                        // MET2-2857/2870: capture the trigger so the modal can
                        // return focus here when it closes — Header re-renders
                        // without this <li> once displayLogIn flips, so we lose
                        // document.activeElement otherwise.
                        captureLoginTrigger(e.currentTarget);
                        showLogIn();
                      }}
                    >
                      <span>Login</span>
                    </button>
                  )}
                  {!userFetchPending && user && user.id && (
                    // QA fix: the dropdown menu (Your Subscription / Help / Gift / Logout)
                    // was removed from the new design. Logged-in users get a simple
                    // "My Account" link matching the mobile drawer; account management +
                    // logout live on the account hub page.
                    <a
                      href={metUrl("/account/my-account-hub/")}
                      className="nav-utility-link nav-utility-link-account"
                    >
                      <span>My Account</span>
                    </a>
                  )}
                  {!userFetchPending && user && !user.id && (
                    <span className="nav-utility-link nav-utility-link-account">
                      <span>{user.institutionName || "Student Access Account"}</span>
                    </span>
                  )}
                </li>

                {/* Donate */}
                <li className="nav-utility-item">
                  <a
                    href={metUrl("/protectthemet")}
                    className="nav-utility-link nav-utility-link-donate"
                  >
                    <span>Donate</span>
                  </a>
                </li>

                {/* Calendar */}
                <li className="nav-utility-item">
                  <a
                    href={metUrl("/calendar/")}
                    className="nav-utility-link nav-utility-link-calendar"
                  >
                    <span>Calendar</span>
                  </a>
                </li>

              </ul>
            </div>
          </div>
        )}

        {/* Main nav bar */}
        <nav aria-label="Primary navigation">
          <div className="header-row nav">

            {/* nav-inner: hamburger (mobile) | logo | search+calendar (mobile) */}
            <div className="nav-inner">

              {/* Hamburger — mobile only, hidden on desktop */}
              {!inMaintenance && !displayLogIn && (
                <button
                  type="button"
                  className="nav-trigger nav-trigger-menu"
                  onClick={menuOpen ? hideMenu : showMenu}
                  aria-expanded={menuOpen}
                  aria-controls="header-nav-menu"
                  aria-label={menuOpen ? "Close menu" : "Open menu"}
                  ref={menuToggleRef}
                >
                  <span className="sr-only">{menuOpen ? "Close menu" : "Menu"}</span>
                </button>
              )}

              {/* Logo */}
              <NavLink to="/" onClick={scrollToTop} exact={true} className="nav-logo">
                <img src={SVGImage.MetOperaLogo} alt="Met Opera On Demand – Home" />
              </NavLink>

              {/* Search + Calendar — mobile only, hidden on desktop */}
              {!inMaintenance && !displayLogIn && (
                <div className="nav-mobile">
                  <NavLink
                    to="/search"
                    className="nav-trigger nav-trigger-search"
                    aria-label="Search"
                    onClick={hideMenu}
                  >
                    <span className="sr-only">Search</span>
                  </NavLink>
                  <a
                    href={metUrl("/calendar/")}
                    className="nav-trigger nav-trigger-calendar"
                    aria-label="Calendar"
                  >
                    <span className="sr-only">Calendar</span>
                  </a>
                </div>
              )}
            </div>

            {/* Desktop primary nav — sibling of nav-inner, hidden on mobile */}
            {!inMaintenance && !displayLogIn && (
              <ul className="nav-item-list nav-desktop" aria-label="Main navigation">
                {headerConfig.map(function(item: NavItem) {
                  return renderNavItem(item, openSubmenuId, openSubmenu, closeOnLeave, toggleSubmenu, closeSubmenu, triggerRefs, scrollToTop, false);
                })}
              </ul>
            )}

          </div>

          {/* Mobile full-screen drawer */}
          {!inMaintenance && !displayLogIn && (
            <div
              id="header-nav-menu"
              className={classnames("nav-menu-drawer", { "nav-menu-is-open": menuOpen })}
              ref={menuContainerRef}
              aria-hidden={!menuOpen}
            >
              {/* Dark login bar at top — matches stage.metopera.org */}
              <ul className="nav-mobile-utility theme-dark" aria-label="Account navigation">
                {!userFetchPending && !user && (
                  <li className="nav-utility-item">
                    <button
                      type="button"
                      className="nav-utility-link nav-utility-link-account"
                      onClick={function(e) {
                        // MET2-2857/2870: capture trigger for focus-return on close
                        captureLoginTrigger(e.currentTarget);
                        handleShowLogIn();
                      }}
                    >
                      <span>Login</span>
                    </button>
                  </li>
                )}
                {!userFetchPending && user && user.id && (
                  <li className="nav-utility-item">
                    <a
                      href={metUrl("/account/my-account-hub/")}
                      className="nav-utility-link nav-utility-link-account"
                    >
                      <span>My Account</span>
                    </a>
                  </li>
                )}
                {!userFetchPending && user && !user.id && (
                  <li className="nav-utility-item">
                    <span className="nav-utility-link nav-utility-link-account">
                      <span>{user.institutionName || "Student Access Account"}</span>
                    </span>
                  </li>
                )}
              </ul>

              {/* Main nav items */}
              <ul className="nav-item-list" aria-label="Mobile navigation">
                {headerConfig.map(function(item: NavItem) {
                  return renderNavItem(item, openSubmenuId, openSubmenu, closeOnLeave, toggleSubmenu, closeSubmenu, triggerRefs, scrollToTop, true);
                })}
              </ul>
            </div>
          )}
        </nav>

      </header>
    </React.Fragment>
  );
}

function renderNavItem(
  item: NavItem,
  openSubmenuId: string | null,
  openSubmenu: (id: string) => void,
  closeOnLeave: (id: string) => void,
  toggleSubmenu: (id: string) => void,
  closeSubmenu: (id: string) => void,
  triggerRefs: React.MutableRefObject<Map<string, HTMLButtonElement>>,
  scrollToTop: () => void,
  isMobile: boolean
): JSX.Element {
  // MET2-2812: single source of truth — openSubmenuId is the only open signal.
  const isOpen = openSubmenuId === item.id;

  if (item.children && item.children.length > 0) {
    return (
      <li
        key={item.id}
        className={classnames("nav-item-parent", { "nav-item-parent-subnav-open": isOpen })}
        onMouseEnter={!isMobile ? function() { openSubmenu(item.id); } : undefined}
        onMouseLeave={!isMobile ? function() { closeOnLeave(item.id); } : undefined}
      >
        {/* Disclosure-style submenu trigger: aria-expanded + aria-haspopup="true"
            + aria-controls is sufficient — no role="menuitem" needed since the
            parent ul is not a role="menubar". */}
        <button
          type="button"
          className="nav-item-parent-item nav-item-parent-btn"
          ref={!isMobile ? function(el) {
            if (el) { triggerRefs.current.set(item.id, el); }
            else { triggerRefs.current.delete(item.id); }
          } : undefined}
          onClick={function() { toggleSubmenu(item.id); }}
          onKeyDown={!isMobile ? function(e) {
            if (e.key === "ArrowDown" || e.key === "Down") {
              e.preventDefault();
              openSubmenu(item.id);
              // Focus first item after React re-renders the open submenu
              setTimeout(function() {
                const submenu = document.getElementById("nav-submenu-" + item.id);
                if (submenu) {
                  const firstItem = submenu.querySelector<HTMLElement>("a[href], button:not([disabled])");
                  if (firstItem) { firstItem.focus(); }
                }
              }, 0);
            }
          } : undefined}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-controls={"nav-submenu-" + item.id}
        >
          {item.text}
        </button>
        <ul
          className="nav-item-child"
          id={"nav-submenu-" + item.id}
          role="menu"
          aria-label={item.text + " submenu"}
          onKeyDown={!isMobile ? function(e) {
            if (e.key === "Escape") {
              e.preventDefault();
              closeSubmenu(item.id);
            }
          } : undefined}
        >
          {item.children.map(function(child) {
            return (
              <li key={child.url} className="nav-item-child-item" role="none">
                {child.internal ? (
                  <NavLink
                    to={child.url}
                    exact={child.url === "/"}
                    className="nav-item-child-item-link"
                    role="menuitem"
                  >
                    {child.text}
                  </NavLink>
                ) : (
                  <a
                    href={child.url}
                    className="nav-item-child-item-link"
                    role="menuitem"
                  >
                    {child.text}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </li>
    );
  }

  if (item.internal) {
    return (
      <li key={item.id} className="nav-item-parent">
        <NavLink
          to={item.url || "/"}
          exact={true}
          className="nav-item-parent-item"
          onClick={scrollToTop}
        >
          {item.text}
        </NavLink>
      </li>
    );
  }

  return (
    <li key={item.id} className="nav-item-parent">
      <a
        href={item.url}
        className="nav-item-parent-item"
      >
        {item.text}
      </a>
    </li>
  );
}

export default compose(withRouter, withStore)(Header);
